import React from "react";
import Modal from "react-bootstrap/Modal";
import Offcanvas from "react-bootstrap/Offcanvas";
import PropTypes from "prop-types";

//services
import { Mixpanel, MixpanelEvents } from "../../../services/Mixpanel";

// css
import * as styles from "./PopUpModal.module.css";

const PopUpModal = ({
  show,
  onHide,
  title,
  children,
  size = "md",
  centered = true,
  backdrop = true,
  keyboard = true,
  showHeader = true,
  showCloseButton = true,
  customHeaderClass = "",
  customBodyClass = "",
  customModalClass = "",
  windowSize,
  showCanvasModal,
  onHideCanvasModal,
  canvasPlacement = "bottom",
  canvasHeight = "auto",
  fullScreen = false,
  scrollable = false,
  animation = true,
  pageName = "Popup Modal",
  ...otherProps
}) => {
  const handleCloseClick = () => {
    Mixpanel.track(MixpanelEvents.pop_up_closed, {
      page_name: pageName,
      info: "Pop Up Modal Closed via Close Button",
    });

    onHide();
  };

  const handleCanvasCloseClick = () => {
    Mixpanel.track(MixpanelEvents.pop_up_closed, {
      page_name: pageName,
      info: "Pop Up Modal Closed via Close Button",
    });

    (onHideCanvasModal || onHide)();
  };

  if (windowSize && windowSize.width <= 768) {
    return (
      <Offcanvas
        show={showCanvasModal || show}
        onHide={onHideCanvasModal || onHide}
        placement={canvasPlacement}
        style={{ height: canvasHeight }}
        backdrop={backdrop}
        keyboard={keyboard}
        {...otherProps}
      >
        {showHeader && (
          <Offcanvas.Header
            className={`${styles.modal_header} ${customHeaderClass}`}
          >
            {title && (
              <Offcanvas.Title className={styles.modal_title}>
                {title}
              </Offcanvas.Title>
            )}
            {showCloseButton && (
              <div
                onClick={handleCanvasCloseClick}
                className={styles.modal_close_btn}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCanvasCloseClick();
                  }
                }}
                aria-label="Close modal"
              >
                <span className="icon-cross" aria-hidden="true"></span>
              </div>
            )}
          </Offcanvas.Header>
        )}
        <Offcanvas.Body className={`${styles.modal_body} ${customBodyClass}`}>
          {children}
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop={backdrop}
      centered={centered}
      size={size}
      keyboard={keyboard}
      fullscreen={fullScreen}
      scrollable={scrollable}
      animation={animation}
      className={customModalClass}
      {...otherProps}
    >
      {showHeader && (
        <Modal.Header className={`${styles.modal_header} ${customHeaderClass}`}>
          {title && (
            <Modal.Title className={styles.modal_title}>{title}</Modal.Title>
          )}
          {showCloseButton && (
            <div
              onClick={handleCloseClick}
              className={styles.modal_close_btn}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCloseClick();
                }
              }}
              aria-label="Close modal"
            >
              <span className="icon-cross" aria-hidden="true"></span>
            </div>
          )}
        </Modal.Header>
      )}
      <Modal.Body className={`${styles.modal_body} ${customBodyClass}`}>
        {children}
      </Modal.Body>
    </Modal>
  );
};

PopUpModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(["sm", "lg", "xl", "md"]),
  centered: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  keyboard: PropTypes.bool,
  showHeader: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  customHeaderClass: PropTypes.string,
  customBodyClass: PropTypes.string,
  customModalClass: PropTypes.string,
  windowSize: PropTypes.object,
  showCanvasModal: PropTypes.bool,
  onHideCanvasModal: PropTypes.func,
  canvasPlacement: PropTypes.oneOf(["top", "bottom", "start", "end"]),
  canvasHeight: PropTypes.string,
  fullScreen: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  scrollable: PropTypes.bool,
  animation: PropTypes.bool,
  pageName: PropTypes.string,
};

export default PopUpModal;
